import { type FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { create } from '@bufbuild/protobuf'
import {
  client as customerClient,
  useFetchColorSimulation,
  useFetchSharedCompanyColors,
  useProcessImage,
  useToggleFavorite,
  useUpdateTitle,
} from '@customer/api/colorSimulationApi'
import { protoColorsToOptions } from '@customer/api/protoColor'
import { ProcessImageType } from '@customer/gen_proto/ai_color_simulation/v1/messages_pb'
import { CheckMaskImagesReadyResponseSchema } from '@customer/gen_proto/api_proto/v1/ai_color_simulation_pb'
import { GetColorSimulationByShareLinkRequestSchema } from '@customer/gen_proto/api_proto/v1/customer_service_pb'
import { Image as ImageIcon } from 'lucide-react'

import { safeDataLayer } from '@modules/utils/gtm'

import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'
import { SimulationDetail } from '@ai-color-modules/template/SimulationDetail/SimulationDetail'
import {
  API_TO_AREA_NAME_MAP,
  BASE_AREAS,
} from '@ai-color-modules/template/SimulationDetail/utils/processParameters'
import { getMaskUrls } from '@ai-color-modules/utils/maskUtils'

/** 施主向けカラーシミュレーション閲覧ページ */
export const ColorSimulationPage: FC = () => {
  const { data, isLoading, error } = useFetchColorSimulation()
  const { data: sharedColorsData } = useFetchSharedCompanyColors(!!data)

  // エリア別の色一覧（area_key ベース）。企業指定プリセットへの拘束を表現する.
  const availableColorsByArea = useMemo<Record<string, ColorOption[]>>(() => {
    if (!sharedColorsData) return {}
    return Object.fromEntries(
      sharedColorsData.areas.map(area => [area.areaKey, protoColorsToOptions(area.colors)]),
    )
  }, [sharedColorsData])

  // フォールバック / AreaColorSelector 用の全エリア union（value 重複除去）.
  const availableColors = useMemo<ColorOption[]>(() => {
    if (!sharedColorsData) return []
    const seen = new Set<string>()
    const union: ColorOption[] = []
    for (const area of sharedColorsData.areas) {
      for (const option of protoColorsToOptions(area.colors)) {
        if (seen.has(option.value)) continue
        seen.add(option.value)
        union.push(option)
      }
    }
    return union
  }, [sharedColorsData])
  const processImageMutation = useProcessImage()
  const toggleFavoriteMutation = useToggleFavorite()
  const updateTitleMutation = useUpdateTitle()
  const gtmSentRef = useRef(false)

  // マスク画像生成完了をチェックする関数（ポーリング用）
  // CustomerService には CheckMaskImagesReady RPC が無いため、
  // GetColorSimulationByShareLink を再取得して画像内のマスク有無を判定する。
  // share-link 認証はサーバ側で interceptor が処理する。
  const checkMaskReady = useCallback(async (imageId: bigint) => {
    const response = await customerClient.getColorSimulationByShareLink(
      create(GetColorSimulationByShareLinkRequestSchema),
    )
    const targetImage = response.simulation?.images.find(img => img.id === imageId)
    const existingTypes = new Set(targetImage?.simulatedImage?.map(simImg => simImg.type) ?? [])
    const hasWallMask = existingTypes.has(ProcessImageType.MASKED_WALL)
    const hasRoofMask = existingTypes.has(ProcessImageType.MASKED_ROOF)
    const hasGutterMask = existingTypes.has(ProcessImageType.MASKED_GUTTER)
    const hasFasciaMask = existingTypes.has(ProcessImageType.MASKED_FASCIA)
    // is_ready は wall/roof/gutter の3種で判定（fasciaは後方互換のため除外）
    const isReady = hasWallMask && hasRoofMask && hasGutterMask
    return create(CheckMaskImagesReadyResponseSchema, {
      isReady,
      hasWallMask,
      hasRoofMask,
      hasGutterMask,
      hasFasciaMask,
    })
  }, [])

  // マスクURLのみを取得する関数（ポーリング完了時に呼ばれる）
  const fetchMaskUrls = useCallback(async (imageId: bigint): Promise<Record<string, string>> => {
    const response = await customerClient.getColorSimulationByShareLink(
      create(GetColorSimulationByShareLinkRequestSchema),
    )
    const targetImage = response.simulation?.images.find(img => img.id === imageId)
    return getMaskUrls(targetImage)
  }, [])

  // GTM: データ取得後にcustomer_id/company_idをdataLayerに送信
  useEffect(() => {
    if (!data || gtmSentRef.current) return
    gtmSentRef.current = true

    const companyId = Number(data.companyId)
    const customerId = Number(data.simulation?.customerId ?? 0)

    // カスタムディメンション登録
    safeDataLayer({
      event: 'setDimension',
      gaCustomerId: customerId,
      gaCompanyId: companyId,
    })
    // Clarity ユーザーID送信
    safeDataLayer({
      event: 'clarity_send_user_id',
      gaCustomerId: customerId,
      gaCompanyId: companyId,
    })
  }, [data])

  const simulation = data?.simulation

  // カット（外観カット=元画像）ごとに、施主が変更できるベースエリアをサーバ解決結果から構築する。
  // 共有時スナップショット 1 カット分ではなく cuts を消費することで、タブ切替に追従させる。
  //
  // 未取得（undefined）の間は prop 自体を undefined にする。SimulationDetail 側は
  // 「prop 未指定 = 全件フォールバック」なので取得完了まで一時的に 4 エリアが出るが、
  // ここで {} を渡すと「全カットが編集不可」という誤った確定表示になってしまうため、
  // 未確定は未指定として扱う。
  const visibleBaseAreasByImageId = useMemo<Record<string, string[]> | undefined>(() => {
    if (!sharedColorsData) return undefined
    return Object.fromEntries(
      sharedColorsData.cuts.map(cut => {
        const editableLabels = new Set(
          cut.editableBaseAreas.map(areaName => API_TO_AREA_NAME_MAP[areaName] ?? areaName),
        )
        return [
          cut.baseImageId.toString(),
          BASE_AREAS.filter(areaLabel => editableLabels.has(areaLabel)),
        ]
      }),
    )
  }, [sharedColorsData])

  // 有効期限の表示
  const expiresAtText = useMemo(() => {
    if (!data?.expiresAt) return ''
    const date = new Date(Number(data.expiresAt.seconds) * 1000)
    return `${date.toLocaleString('ja-JP')} まで有効`
  }, [data?.expiresAt])

  const handleToggleFavorite = useCallback(
    async (imageId: number, favorited: boolean) => {
      await toggleFavoriteMutation.mutateAsync({ imageId, favorited })
    },
    [toggleFavoriteMutation],
  )

  const handleUpdateTitle = useCallback(
    async (imageId: number, title: string) => {
      await updateTitleMutation.mutateAsync({ imageId, title })
    },
    [updateTitleMutation],
  )

  // 共有リンクのアンカー（施主が着地する履歴。issue #2950）。0 はプロジェクト単位のリンク
  const sharedAnchor = useMemo(
    () =>
      data && data.anchorImageId !== BigInt(0)
        ? { imageId: data.anchorImageId, companyName: data.companyName }
        : undefined,
    [data],
  )

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <p className="text-zinc-500">読み込み中...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="text-center">
          <p className="text-lg font-medium text-zinc-900">リンクが無効です</p>
          <p className="mt-2 text-sm text-zinc-500">
            共有リンクの有効期限が切れているか、URLが正しくありません。
          </p>
        </div>
      </div>
    )
  }

  if (!simulation || (simulation.images?.length ?? 0) === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <div className="text-center">
          <ImageIcon className="mx-auto h-12 w-12 text-zinc-300" />
          <p className="mt-4 text-zinc-500">シミュレーション画像がありません</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* ヘッダー */}
      <header className="border-b bg-white px-4 py-4 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-lg font-semibold text-zinc-900">
            {simulation.dealName || 'カラーシミュレーション'}
          </h1>
          {expiresAtText && <p className="mt-1 text-xs text-zinc-500">{expiresAtText}</p>}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <SimulationDetail
          availableColors={availableColors}
          availableColorsByArea={availableColorsByArea}
          data={simulation}
          role="customer"
          processImageMutation={processImageMutation}
          onToggleImageFavorite={handleToggleFavorite}
          onUpdateImageTitle={handleUpdateTitle}
          sharedAnchor={sharedAnchor}
          checkMaskReady={checkMaskReady}
          fetchMaskUrls={fetchMaskUrls}
          visibleBaseAreasByImageId={visibleBaseAreasByImageId}
        />
      </main>
    </div>
  )
}
